import {
  useEffect,
  useRef,
  useState,
} from 'react';

import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';

import { useAuth } from '../contexts/AuthContext';
import SEO from '../components/SEO';

import {
  ArrowRight,
  Brain,
  Buildings,
  Check,
  ChartLineUp,
  Crown,
  EnvelopeSimple,
  Fire,
  Lightning,
  Rocket,
  Shield,
  Sparkle,
  Star,
  Users,
  X,
} from '@phosphor-icons/react';

import API_URL from '../utils/apiUrl';
import { trackEvent, EVT } from '../utils/analytics';

const MONEY_FORMATTER = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
  minimumFractionDigits: 2,
});

function getSchoolYearStage() {
  const now = new Date();
  const month = now.getMonth();

  if (month >= 8 && month <= 10) {
    return {
      badge: 'Back to school • GCSE 2027 prep',
      heading: 'Pick the plan that helps your child start the new term strong',
      description: 'A strong school year starts with a structure that makes revision consistent, measurable and realistic for GCSE 2027.',
    };
  }

  if (month >= 11 || month <= 1) {
    return {
      badge: 'Winter term • support the basics',
      heading: 'Choose the plan that helps your child tighten the gaps now',
      description: 'The best revision support in winter is consistent practice, clear feedback and a realistic plan that keeps GCSE 2027 goals visible.',
    };
  }

  if (month >= 2 && month <= 4) {
    return {
      badge: 'Spring term • mock season is coming',
      heading: 'Choose the plan that helps your child sharpen revision before mocks',
      description: 'The spring term is where targeted practice and parent visibility matter most, especially for GCSE 2027 readiness.',
    };
  }

  if (month >= 5 && month <= 6) {
    return {
      badge: 'Summer prep • build for 2027',
      heading: 'Choose the plan that makes the summer a productive head start',
      description: 'Summer is an ideal time to build confidence, fix weak areas and keep momentum going before the next GCSE cycle.',
    };
  }

  return {
    badge: 'Summer reset • ready for the new year',
    heading: 'Choose the plan that gives your child a stronger start to the next GCSE year',
    description: 'The right plan now helps students regain momentum, rebuild confidence and get a clearer path through the 2027 GCSE cycle.',
  };
}

const FREE_PLAN = {
  id: 'free',
  name: '7-Day Free Sampler',
  subtitle: 'Try Grade9Ready free before choosing a parent plan.',
  icon: Star,
  color: '#6b7280',
  features: [
    'Section 1 access in Maths, English, Science and Geography',
    'Foundation GCSE Problem Mode',
    'Five marked answers',
    'Student progress tracking and badges',
    'End-of-section tests',
    'No payment card required',
  ],
  cta: 'Create Parent Account',
  ctaLink: '/signup',
};

const PAID_PLANS = [
  {
    id: 'standard',
    name: 'Essentials',
    audience: 'For one pupil',
    subtitle: 'Ideal for a single child who needs full lesson access and AI feedback.',
    monthlyPrice: 4.99,
    annualPrice: 47.99,
    icon: Lightning,
    color: '#6366f1',
    badge: 'Best starting plan',
    features: [
      {
        text: 'All currently available lessons',
        included: true,
      },
      {
        text: 'New subjects and lessons as they are released',
        included: true,
      },
      {
        text: 'Foundation and Higher GCSE Problem Mode',
        included: true,
      },
      {
        text: 'Mixed-topic exam practice and mock exams',
        included: true,
      },
      {
        text: '50 marked answers each month',
        included: true,
      },
      {
        text: 'Student progress dashboard',
        included: true,
      },
      {
        text: 'Badges and achievements',
        included: true,
      },
      {
        text: 'One child account',
        included: true,
      },
      {
        text: 'Detailed parent readiness dashboard',
        included: false,
      },
      {
        text: 'Personalised study plans',
        included: false,
      },
      {
        text: 'Mock exam builder',
        included: false,
      },
    ],
    cta: 'Choose Essentials',
  },
  {
    id: 'premium',
    name: 'Complete',
    audience: 'For one pupil with deeper feedback',
    subtitle: 'Best for focused revision, detailed progress insight and parent reporting.',
    monthlyPrice: 7.99,
    annualPrice: 76.99,
    icon: Crown,
    color: '#f59e0b',
    badge: 'Full individual plan',
    featured: true,
    features: [
      {
        text: 'Everything in Essentials',
        included: true,
      },
      {
        text: 'Unlimited GCSE Problem Mode',
        included: true,
      },
      {
        text: 'Unlimited marked answers',
        included: true,
      },
      {
        text: 'Detailed WWW and EBI feedback',
        included: true,
      },
      {
        text: 'Parent GCSE-readiness dashboard',
        included: true,
      },
      {
        text: 'Foundation and Higher readiness scores',
        included: true,
      },
      {
        text: 'Weak-area reporting',
        included: true,
      },
      {
        text: 'Personalised study plans',
        included: true,
      },
      {
        text: 'Mock exam builder',
        included: true,
      },
      {
        text: 'Study schedule controls',
        included: true,
      },
      {
        text: 'One child account',
        included: true,
      },
    ],
    cta: 'Choose Complete',
    // Selecting two children swaps this plan to the family package/price below.
    familyVariant: {
      packageId: 'family',
      monthlyPrice: 9.99,
      annualPrice: 95.99,
      childFeatureText: 'Two child accounts',
    },
  },
];

const EDUCATION_PLAN = {
  id: 'education',
  name: 'Education Provider',
  audience: 'For schools, MATs and tutoring centres',
  subtitle: 'Flexible pricing based on your setting and student numbers.',
  icon: Buildings,
  color: '#3b82f6',
  features: [
    'Flexible teacher and student numbers',
    'Teacher administration dashboard',
    'Class and cohort progress analytics',
    'Bulk student onboarding',
    'Assign students to teachers or groups',
    'Custom implementation support',
    'Optional branding arrangements',
    'Dedicated account contact',
  ],
  cta: 'Request a Proposal',
};

function formatMoney(value) {
  return MONEY_FORMATTER.format(value);
}

function getAnnualSaving(plan) {
  return plan.monthlyPrice * 12 - plan.annualPrice;
}

function getMonthlyEquivalent(plan) {
  return plan.annualPrice / 12;
}

function Reveal({
  children,
  className = '',
  delay = 0,
}) {
  const reduceMotion = useReducedMotion();

  return (
    <motion.div
      initial={
        reduceMotion
          ? false
          : {
              opacity: 0,
              y: 18,
            }
      }
      whileInView={
        reduceMotion
          ? undefined
          : {
              opacity: 1,
              y: 0,
            }
      }
      viewport={{
        once: true,
        amount: 0.15,
      }}
      transition={{
        duration: 0.4,
        delay,
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function EducationEnquiryModal({ onClose }) {
  const titleId = 'education-enquiry-title';
  const descriptionId = 'education-enquiry-description';

  const dialogRef = useRef(null);
  const firstFieldRef = useRef(null);

  const [form, setForm] = useState({
    organisation: '',
    contact_name: '',
    email: '',
    role: '',
    student_count: '',
    message: '',
  });

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const previouslyFocusedElement = document.activeElement;
    const originalOverflow = document.body.style.overflow;

    document.body.style.overflow = 'hidden';

    window.setTimeout(() => {
      firstFieldRef.current?.focus();
    }, 0);

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }

      if (event.key !== 'Tab') {
        return;
      }

      const focusableElements =
        dialogRef.current?.querySelectorAll(
          [
            'button:not([disabled])',
            'input:not([disabled])',
            'textarea:not([disabled])',
            'select:not([disabled])',
            'a[href]',
            '[tabindex]:not([tabindex="-1"])',
          ].join(','),
        ) || [];

      const elements = Array.from(focusableElements);

      if (elements.length === 0) {
        return;
      }

      const firstElement = elements[0];
      const lastElement = elements[elements.length - 1];

      if (
        event.shiftKey &&
        document.activeElement === firstElement
      ) {
        event.preventDefault();
        lastElement.focus();
      } else if (
        !event.shiftKey &&
        document.activeElement === lastElement
      ) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      document.removeEventListener('keydown', onKeyDown);
      previouslyFocusedElement?.focus?.();
    };
  }, [onClose]);

  const updateField = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (
      !form.organisation.trim() ||
      !form.contact_name.trim() ||
      !form.email.trim()
    ) {
      setError(
        'Please enter your organisation, name and work email.',
      );
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch(
        `${API_URL}/api/education/enquiry`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...form,
            organisation: form.organisation.trim(),
            contact_name: form.contact_name.trim(),
            email: form.email.trim(),
            role: form.role.trim(),
            message: form.message.trim(),
          }),
        },
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          data.detail ||
            data.message ||
            'We could not send your enquiry.',
        );
      }

      trackEvent(EVT.CTA_CLICK, {
        cta: 'submit_education_enquiry',
        location: 'pricing_education_modal',
      });

      setSubmitted(true);
    } catch (submitError) {
      setError(
        submitError.message ||
          'We could not send your enquiry. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
      data-testid="education-modal-backdrop"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        className="neo-card bg-card max-w-xl w-full max-h-[90vh] overflow-y-auto"
        data-testid="education-modal"
      >
        {submitted ? (
          <div className="text-center py-6">
            <div className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-700 border-2 border-foreground flex items-center justify-center mx-auto mb-5">
              <Check size={28} weight="bold" />
            </div>

            <h2
              id={titleId}
              className="font-heading font-black text-2xl mb-3"
            >
              Thanks — your enquiry has been sent
            </h2>

            <p
              id={descriptionId}
              className="text-muted-foreground mb-6"
            >
              A member of the Grade9Ready team will review your
              requirements and contact you about a demonstration and
              tailored proposal.
            </p>

            <button
              type="button"
              onClick={onClose}
              className="neo-btn px-5 py-2.5"
              data-testid="education-modal-close"
            >
              Close
            </button>
          </div>
        ) : (
          <form
            onSubmit={onSubmit}
            className="space-y-5"
            noValidate
          >
            <div>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2
                    id={titleId}
                    className="font-heading font-black text-2xl mb-2"
                  >
                    Request an education proposal
                  </h2>

                  <p
                    id={descriptionId}
                    className="text-sm text-muted-foreground"
                  >
                    Tell us about your school, trust or tutoring
                    setting and the number of learners you would like
                    to support.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={onClose}
                  className="w-9 h-9 rounded-lg border-2 border-foreground flex items-center justify-center font-black hover:bg-secondary shrink-0"
                  aria-label="Close enquiry form"
                >
                  <X size={18} weight="bold" />
                </button>
              </div>
            </div>

            <div>
              <label
                htmlFor="education-organisation"
                className="block text-sm font-bold mb-1.5"
              >
                School or organisation
              </label>

              <input
                ref={firstFieldRef}
                id="education-organisation"
                name="organisation"
                required
                autoComplete="organization"
                className="neo-input w-full"
                value={form.organisation}
                onChange={(event) =>
                  updateField(
                    'organisation',
                    event.target.value,
                  )
                }
                data-testid="edu-organisation"
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="education-contact-name"
                  className="block text-sm font-bold mb-1.5"
                >
                  Your name
                </label>

                <input
                  id="education-contact-name"
                  name="contact_name"
                  required
                  autoComplete="name"
                  className="neo-input w-full"
                  value={form.contact_name}
                  onChange={(event) =>
                    updateField(
                      'contact_name',
                      event.target.value,
                    )
                  }
                  data-testid="edu-name"
                />
              </div>

              <div>
                <label
                  htmlFor="education-role"
                  className="block text-sm font-bold mb-1.5"
                >
                  Your role
                </label>

                <input
                  id="education-role"
                  name="role"
                  autoComplete="organization-title"
                  className="neo-input w-full"
                  placeholder="For example, Head of Maths"
                  value={form.role}
                  onChange={(event) =>
                    updateField('role', event.target.value)
                  }
                  data-testid="edu-role"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="education-email"
                  className="block text-sm font-bold mb-1.5"
                >
                  Work email
                </label>

                <input
                  id="education-email"
                  name="email"
                  type="email"
                  required
                  autoComplete="email"
                  className="neo-input w-full"
                  value={form.email}
                  onChange={(event) =>
                    updateField('email', event.target.value)
                  }
                  data-testid="edu-email"
                />
              </div>

              <div>
                <label
                  htmlFor="education-student-count"
                  className="block text-sm font-bold mb-1.5"
                >
                  Approximate student numbers
                </label>

                <input
                  id="education-student-count"
                  name="student_count"
                  type="number"
                  min="1"
                  inputMode="numeric"
                  className="neo-input w-full"
                  value={form.student_count}
                  onChange={(event) =>
                    updateField(
                      'student_count',
                      event.target.value,
                    )
                  }
                  data-testid="edu-students"
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="education-message"
                className="block text-sm font-bold mb-1.5"
              >
                What would you like to achieve?
              </label>

              <textarea
                id="education-message"
                name="message"
                rows={4}
                className="neo-input w-full"
                placeholder="Include year groups, subjects, timelines or any implementation requirements."
                value={form.message}
                onChange={(event) =>
                  updateField('message', event.target.value)
                }
                data-testid="edu-message"
              />
            </div>

            <p className="text-xs text-muted-foreground">
              We will only use these details to respond to your
              enquiry. See our{' '}
              <Link
                to="/privacy"
                className="underline font-bold"
              >
                privacy notice
              </Link>
              .
            </p>

            {error && (
              <div
                role="alert"
                className="rounded-xl border-2 border-destructive bg-destructive/10 p-3 text-sm font-bold text-destructive"
                data-testid="edu-error"
              >
                {error}
              </div>
            )}

            <div className="flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
              <button
                type="button"
                onClick={onClose}
                className="neo-btn-secondary py-2.5 px-4 text-sm"
                data-testid="edu-cancel"
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={submitting}
                className="neo-btn py-2.5 px-5 text-sm disabled:opacity-60 disabled:cursor-wait"
                data-testid="edu-submit"
              >
                {submitting
                  ? 'Sending enquiry…'
                  : 'Send Enquiry'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function BillingToggle({
  annual,
  onChange,
}) {
  return (
    <div
      className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4"
      data-testid="billing-toggle"
    >
      <span
        className={`text-sm font-bold ${
          !annual
            ? 'text-foreground'
            : 'text-muted-foreground'
        }`}
      >
        Pay monthly
      </span>

      <button
        type="button"
        role="switch"
        aria-checked={annual}
        aria-label="Use annual billing"
        onClick={() => onChange(!annual)}
        className={`relative w-16 h-8 rounded-full border-2 border-foreground transition-colors ${
          annual ? 'bg-primary' : 'bg-secondary'
        }`}
        data-testid="toggle-annual"
      >
        <span
          aria-hidden="true"
          className={`absolute top-0.5 w-6 h-6 rounded-full bg-white border-2 border-foreground transition-transform ${
            annual ? 'translate-x-8' : 'translate-x-0.5'
          }`}
        />
      </button>

      <span
        className={`text-sm font-bold ${
          annual
            ? 'text-foreground'
            : 'text-muted-foreground'
        }`}
      >
        Pay annually
        <span className="ml-2 text-primary font-black">
          save about 20%
        </span>
      </span>
    </div>
  );
}

function PriceDisplay({
  plan,
  annual,
}) {
  if (!annual) {
    return (
      <div>
        <div className="flex items-baseline gap-1">
          <span className="text-4xl font-black">
            {formatMoney(plan.monthlyPrice)}
          </span>

          <span className="text-sm text-muted-foreground">
            /month
          </span>
        </div>

        <p className="text-xs text-muted-foreground mt-1">
          Billed monthly
        </p>
      </div>
    );
  }

  const monthlyEquivalent = getMonthlyEquivalent(plan);
  const annualSaving = getAnnualSaving(plan);

  return (
    <div>
      <div className="flex items-baseline gap-1">
        <span className="text-4xl font-black">
          {formatMoney(plan.annualPrice)}
        </span>

        <span className="text-sm text-muted-foreground">
          /year
        </span>
      </div>

      <p className="text-sm font-bold mt-1">
        Equivalent to {formatMoney(monthlyEquivalent)}/month
      </p>

      <p className="text-xs text-emerald-600 font-bold mt-1">
        Save {formatMoney(annualSaving)} each year
      </p>
    </div>
  );
}

function ChildCountToggle({
  childCount,
  onChange,
}) {
  return (
    <div
      className="inline-flex items-center gap-1 rounded-xl border-2 border-foreground p-1 mb-4"
      role="group"
      aria-label="Number of child accounts"
      data-testid="child-count-toggle"
    >
      {[1, 2].map((count) => (
        <button
          key={count}
          type="button"
          onClick={() => onChange(count)}
          aria-pressed={childCount === count}
          className={`px-3 py-1.5 rounded-lg text-xs font-black transition-colors ${
            childCount === count
              ? 'bg-primary text-white'
              : 'hover:bg-secondary'
          }`}
          data-testid={`child-count-${count}`}
        >
          <Users size={13} weight="bold" className="inline mr-1 -mt-0.5" />
          {count} {count === 1 ? 'child' : 'children'}
        </button>
      ))}
    </div>
  );
}

function PaidPlanCard({
  plan,
  annual,
  checkoutLoading,
  onCheckout,
  childCount,
  onChildCountChange,
}) {
  const isFamilySelected =
    Boolean(plan.familyVariant) && childCount === 2;

  const effectivePlan = isFamilySelected
    ? {
        ...plan,
        monthlyPrice: plan.familyVariant.monthlyPrice,
        annualPrice: plan.familyVariant.annualPrice,
        features: plan.features.map((feature) =>
          feature.text === 'One child account'
            ? { ...feature, text: plan.familyVariant.childFeatureText }
            : feature,
        ),
      }
    : plan;

  const packageId = isFamilySelected
    ? plan.familyVariant.packageId
    : plan.id;

  const Icon = plan.icon;
  const isLoading = checkoutLoading === packageId;

  return (
    <Reveal className="h-full">
      <article
        className={`neo-card relative flex flex-col h-full ${
          plan.featured
            ? 'border-primary ring-2 ring-primary/20'
            : ''
        }`}
        data-testid={`tier-${plan.id}`}
      >
        {plan.badge && (
          <div className="absolute -top-3 left-5">
            <span
              className={`inline-flex items-center gap-1 px-3 py-1 text-xs font-black rounded-full border-2 border-foreground shadow-[2px_2px_0px_0px_rgb(var(--9ready-shadow))] ${
                plan.featured
                  ? 'bg-primary text-white'
                  : 'bg-accent text-foreground'
              }`}
            >
              <Sparkle size={12} weight="fill" />
              {plan.badge}
            </span>
          </div>
        )}

        <div className="pt-3 mb-5">
          <div className="flex items-start gap-3">
            <div
              className="w-11 h-11 rounded-xl flex items-center justify-center border-2 border-foreground shrink-0"
              style={{
                backgroundColor: `${plan.color}20`,
              }}
            >
              <Icon
                size={23}
                weight="bold"
                style={{
                  color: plan.color,
                }}
              />
            </div>

            <div>
              <h2 className="font-heading font-black text-2xl">
                {plan.name}
              </h2>

              <p className="text-sm font-bold mt-0.5">
                {plan.audience}
              </p>
            </div>
          </div>

          <p className="text-xs text-muted-foreground mt-3">
            {plan.subtitle}
          </p>
        </div>

        {plan.familyVariant && (
          <ChildCountToggle
            childCount={childCount}
            onChange={onChildCountChange}
          />
        )}

        <div className="mb-6">
          <PriceDisplay
            plan={effectivePlan}
            annual={annual}
          />

          {isFamilySelected && (
            <p className="text-xs text-muted-foreground mt-2">
              Cheaper than two separate Complete subscriptions.
            </p>
          )}
        </div>

        <ul className="space-y-3 mb-7 flex-1">
          {effectivePlan.features.map((feature) => (
            <li
              key={feature.text}
              className="flex items-start gap-2"
            >
              {feature.included ? (
                <Check
                  size={17}
                  weight="bold"
                  className="text-emerald-500 shrink-0 mt-0.5"
                />
              ) : (
                <X
                  size={17}
                  weight="bold"
                  className="text-muted-foreground/35 shrink-0 mt-0.5"
                />
              )}

              <span
                className={`text-sm ${
                  feature.included
                    ? ''
                    : 'text-muted-foreground/55'
                }`}
              >
                {feature.text}
              </span>
            </li>
          ))}
        </ul>

        <button
          type="button"
          onClick={() => onCheckout(packageId)}
          disabled={isLoading}
          aria-busy={isLoading}
          className={`block w-full text-center py-3 rounded-xl font-black text-sm border-2 border-foreground transition-all hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-wait ${
            plan.featured
              ? 'bg-primary text-white shadow-[3px_3px_0px_0px_rgb(var(--9ready-shadow))]'
              : 'bg-card hover:bg-secondary shadow-[2px_2px_0px_0px_rgb(var(--9ready-shadow))]'
          }`}
          data-testid={`cta-${plan.id}`}
        >
          {isLoading
            ? 'Opening Secure Checkout…'
            : plan.cta}
        </button>
      </article>
    </Reveal>
  );
}

export default function Pricing() {
  const { user } = useAuth();
  const reduceMotion = useReducedMotion();

  const [annual, setAnnual] = useState(false);
  const [childCount, setChildCount] = useState(1);
  const [checkoutLoading, setCheckoutLoading] =
    useState(null);
  const [checkoutError, setCheckoutError] =
    useState('');

  const [educationOpen, setEducationOpen] =
    useState(false);

  const [launchStatus, setLaunchStatus] =
    useState(null);

  const [waitlistEmail, setWaitlistEmail] =
    useState('');
  const [waitlistName, setWaitlistName] =
    useState('');
  const [waitlistStatus, setWaitlistStatus] =
    useState(null);
  const [waitlistLoading, setWaitlistLoading] =
    useState(false);

  useEffect(() => {
    const controller = new AbortController();

    fetch(`${API_URL}/api/launch/status`, {
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error('Launch status unavailable');
        }

        return response.json();
      })
      .then(setLaunchStatus)
      .catch((error) => {
        if (error.name !== 'AbortError') {
          console.error(
            'Could not load launch status:',
            error,
          );
        }
      });

    return () => {
      controller.abort();
    };
  }, []);

  const offerActive =
    launchStatus?.offer_active === true;

  const trackCta = (cta, location, extra = {}) => {
    trackEvent(EVT.CTA_CLICK, {
      cta,
      location,
      ...extra,
    });
  };

  const joinWaitlist = async (event) => {
    event.preventDefault();

    setWaitlistLoading(true);
    setWaitlistStatus(null);

    try {
      const response = await fetch(
        `${API_URL}/api/launch/waitlist`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: waitlistEmail.trim(),
            name: waitlistName.trim(),
          }),
        },
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          data.detail ||
            data.message ||
            'We could not add you to the waitlist.',
        );
      }

      setWaitlistStatus({
        type: 'success',
        text:
          data.message ||
          'You have been added to the waitlist.',
      });

      setWaitlistEmail('');
      setWaitlistName('');

      trackCta(
        'join_founding_offer_waitlist',
        'pricing_waitlist',
      );
    } catch (error) {
      setWaitlistStatus({
        type: 'error',
        text:
          error.message ||
          'Something went wrong. Please try again.',
      });
    } finally {
      setWaitlistLoading(false);
    }
  };

  const startCheckout = async (packageId) => {
    setCheckoutError('');

    if (user?.role === 'student') {
      setCheckoutError(
        'Subscriptions must be managed through a parent account. Ask a parent or carer to sign in and select the plan.',
      );
      return;
    }

    const billing = annual ? 'annual' : 'monthly';

    trackCta(
      `choose_${packageId}`,
      'pricing_plan',
      {
        billing,
      },
    );

    const returnPath = `/pricing?plan=${encodeURIComponent(
      packageId,
    )}&billing=${billing}`;

    if (!user) {
      window.location.href = `/login?next=${encodeURIComponent(
        returnPath,
      )}`;
      return;
    }

    if (checkoutLoading) {
      return;
    }

    setCheckoutLoading(packageId);

    try {
      const response = await fetch(
        `${API_URL}/api/billing/create-checkout-session`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            package_id: packageId,
            billing,
            origin_url: window.location.origin,
          }),
        },
      );

      const data = await response.json().catch(() => ({}));

      if (response.status === 401) {
        window.location.href = `/login?next=${encodeURIComponent(
          returnPath,
        )}`;
        return;
      }

      if (response.status === 403) {
        throw new Error(
          data.detail ||
            'This account cannot manage subscriptions.',
        );
      }

      if (!response.ok) {
        throw new Error(
          data.detail ||
            data.message ||
            'Checkout could not be started.',
        );
      }

      if (!data.url) {
        throw new Error(
          'The checkout link was not returned.',
        );
      }

      window.location.href = data.url;
    } catch (error) {
      console.error('Checkout error:', error);

      setCheckoutError(
        error.message ||
          'We could not start checkout. Please try again.',
      );

      setCheckoutLoading(null);
    }
  };

  return (
    <div
      className="min-h-screen"
      data-testid="pricing-page"
    >
      <SEO
        title={`${stage.badge} | Grade9Ready Pricing`}
        description={`${stage.description} Compare UK GCSE revision plans for Year 10 and Year 11 students and find a parent-friendly path for Maths, English, Science and Geography.`}
        path="/pricing"
        keywords="GCSE revision plans UK, GCSE revision pricing, Year 10 GCSE support, Year 11 revision plans, parent GCSE plans, maths revision, English revision, science revision"
        geo="United Kingdom"
      />

      {/* Hero */}
      <section className="relative overflow-hidden border-b-2 border-foreground/10">
        <div
          className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(99,102,241,0.18),transparent_38%),radial-gradient(circle_at_85%_15%,rgba(250,204,21,0.20),transparent_28%)]"
          aria-hidden="true"
        />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
          <motion.div
            initial={
              reduceMotion
                ? false
                : {
                    opacity: 0,
                    y: 18,
                  }
            }
            animate={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              duration: 0.45,
            }}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="inline-flex items-center gap-2 bg-accent border-2 border-foreground rounded-full px-4 py-2 mb-6 shadow-[2px_2px_0px_0px_rgb(var(--9ready-shadow))]">
              <Sparkle size={16} weight="fill" />

              <span className="text-xs sm:text-sm font-black uppercase tracking-wide">
                {stage.badge}
              </span>
            </div>

            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-heading font-black tracking-tight leading-[1.05] mb-5"
              data-testid="pricing-heading"
            >
              {stage.heading}
            </h1>

            <p className="text-lg text-muted-foreground max-w-3xl mx-auto mb-7 leading-relaxed">
              {stage.description}
            </p>

            <ul
              className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-sm font-bold"
              data-testid="pricing-trust-strip"
            >
              {[
                'No card for the free sampler',
                'Cancel paid plans anytime',
                'Parent account with pupil slots based on plan',
                'Maths, English, Science and Geography',
              ].map((item) => (
                <li
                  key={item}
                  className="inline-flex items-center gap-2"
                >
                  <Check
                    size={17}
                    weight="bold"
                    className="text-emerald-500"
                  />

                  {item}
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="mb-10 rounded-2xl border-2 border-dashed border-foreground/40 bg-muted/20 p-5 text-center">
          <p className="text-sm font-bold uppercase tracking-[0.18em] text-primary">Parent plans</p>
          <p className="mt-2 text-base text-muted-foreground max-w-2xl mx-auto">
            Parent accounts are the one signup flow. The number of pupil accounts available depends on the subscription you choose, and you can start with the free sampler before upgrading.
          </p>
        </div>

        {/* Student notice */}
        {user?.role === 'student' && (
          <div
            className="max-w-3xl mx-auto mb-10 rounded-2xl border-2 border-foreground bg-primary/10 p-5 text-center"
            data-testid="student-pricing-notice"
          >
            <p className="font-heading font-black text-lg">
              Subscriptions are managed by parents or carers
            </p>

            <p className="text-sm text-muted-foreground mt-2">
              You can compare plans on this page, but a parent account
              must complete any subscription, upgrade or
              cancellation.
            </p>
          </div>
        )}

        {/* Free sampler */}
        <section
          className="mb-16"
          aria-labelledby="free-sampler-heading"
        >
          <Reveal className="text-center mb-8">
            <p className="text-xs font-black uppercase tracking-[0.2em] text-primary mb-3">
              Explore before paying
            </p>

            <h2
              id="free-sampler-heading"
              className="text-3xl sm:text-4xl font-heading font-black mb-3"
            >
              Start with the free seven-day sampler
            </h2>

            <p className="text-muted-foreground max-w-2xl mx-auto">
              The sampler is a separate free access period. It does
              not start a paid subscription and no card details are
              collected.
            </p>
          </Reveal>

          <Reveal>
            <article
              className="neo-card relative flex flex-col lg:flex-row lg:items-center gap-7 border-primary ring-2 ring-primary/20 max-w-5xl mx-auto"
              data-testid="tier-free"
            >
              <div className="flex-1">
                <div className="flex items-start gap-3 mb-4">
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center border-2 border-foreground shrink-0"
                    style={{
                      backgroundColor: `${FREE_PLAN.color}20`,
                    }}
                  >
                    <FREE_PLAN.icon
                      size={23}
                      weight="bold"
                      style={{
                        color: FREE_PLAN.color,
                      }}
                    />
                  </div>

                  <div>
                    <h3 className="font-heading font-black text-2xl">
                      {FREE_PLAN.name}
                    </h3>

                    <p className="text-sm text-muted-foreground mt-1">
                      {FREE_PLAN.subtitle}
                    </p>
                  </div>
                </div>

                <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
                  {FREE_PLAN.features.map((feature) => (
                    <li
                      key={feature}
                      className="flex items-start gap-2"
                    >
                      <Check
                        size={17}
                        weight="bold"
                        className="text-emerald-500 shrink-0 mt-0.5"
                      />

                      <span className="text-sm">
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="lg:w-64 shrink-0">
                <div className="text-4xl font-black mb-1">
                  Free
                </div>

                <p className="text-xs text-muted-foreground mb-4">
                  Seven days · no card · no automatic charge
                </p>

                <Link
                  to={FREE_PLAN.ctaLink}
                  onClick={() =>
                    trackCta(
                      'start_free_sampler',
                      'pricing_free_sampler',
                    )
                  }
                  className="block w-full text-center py-3 rounded-xl font-black text-sm border-2 border-foreground bg-primary text-white shadow-[3px_3px_0px_0px_rgb(var(--9ready-shadow))] hover:-translate-y-0.5 transition-transform"
                  data-testid="cta-free"
                >
                  {FREE_PLAN.cta}
                </Link>
              </div>
            </article>
          </Reveal>
        </section>

        {/* Paid plans */}
        <section
          className="mb-16"
          aria-labelledby="individual-plans-heading"
        >
          <Reveal className="text-center mb-8">
            <p className="text-xs font-black uppercase tracking-[0.2em] text-primary mb-3">
              Individual plans
            </p>

            <h2
              id="individual-plans-heading"
              className="text-3xl sm:text-4xl font-heading font-black mb-3"
            >
              Choose the level of support your child needs
            </h2>

            <p className="text-muted-foreground max-w-2xl mx-auto mb-7">
              Both paid plans unlock the complete learning library.
              Complete adds unlimited feedback, more detailed
              parent tools, and can cover two children on one
              subscription.
            </p>

            <BillingToggle
              annual={annual}
              onChange={setAnnual}
            />
          </Reveal>

          {checkoutError && (
            <div
              role="alert"
              className="max-w-3xl mx-auto mb-7 rounded-xl border-2 border-destructive bg-destructive/10 p-4 text-sm font-bold text-destructive text-center"
              data-testid="checkout-error"
            >
              {checkoutError}
            </div>
          )}

          <div
            className="grid lg:grid-cols-2 gap-6 max-w-5xl mx-auto"
            data-testid="main-tiers"
          >
            {PAID_PLANS.map((plan) => (
              <PaidPlanCard
                key={plan.id}
                plan={plan}
                annual={annual}
                checkoutLoading={checkoutLoading}
                onCheckout={startCheckout}
                childCount={plan.familyVariant ? childCount : 1}
                onChildCountChange={setChildCount}
              />
            ))}
          </div>
        </section>

        {/* Education */}
        <section
          className="mb-16"
          aria-labelledby="education-plan-heading"
        >
          <Reveal>
            <article
              className="neo-card max-w-5xl mx-auto bg-blue-50/50 dark:bg-blue-950/15"
              data-testid="tier-education"
            >
              <div className="grid lg:grid-cols-[1fr_auto] gap-8 items-center">
                <div>
                  <div className="flex items-start gap-3 mb-4">
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center border-2 border-foreground shrink-0"
                      style={{
                        backgroundColor: `${EDUCATION_PLAN.color}20`,
                      }}
                    >
                      <EDUCATION_PLAN.icon
                        size={25}
                        weight="bold"
                        style={{
                          color: EDUCATION_PLAN.color,
                        }}
                      />
                    </div>

                    <div>
                      <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-600 mb-1">
                        Schools and tutoring settings
                      </p>

                      <h2
                        id="education-plan-heading"
                        className="font-heading font-black text-3xl"
                      >
                        {EDUCATION_PLAN.name}
                      </h2>

                      <p className="text-sm font-bold mt-1">
                        {EDUCATION_PLAN.audience}
                      </p>
                    </div>
                  </div>

                  <p className="text-sm text-muted-foreground mb-5">
                    {EDUCATION_PLAN.subtitle}
                  </p>

                  <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
                    {EDUCATION_PLAN.features.map(
                      (feature) => (
                        <div
                          key={feature}
                          className="flex items-start gap-2"
                        >
                          <Check
                            size={17}
                            weight="bold"
                            className="text-emerald-500 shrink-0 mt-0.5"
                          />

                          <span className="text-sm">
                            {feature}
                          </span>
                        </div>
                      ),
                    )}
                  </div>
                </div>

                <div className="lg:w-64">
                  <div className="text-3xl font-black">
                    Custom pricing
                  </div>

                  <p className="text-sm text-muted-foreground mt-2 mb-5">
                    Based on learner numbers, features and
                    implementation requirements.
                  </p>

                  <button
                    type="button"
                    onClick={() => {
                      setEducationOpen(true);

                      trackCta(
                        'open_education_enquiry',
                        'pricing_education_plan',
                      );
                    }}
                    className="block w-full text-center py-3 rounded-xl font-black text-sm border-2 border-foreground bg-blue-500 text-white shadow-[3px_3px_0px_0px_rgb(var(--9ready-shadow))] hover:-translate-y-0.5 transition-transform"
                    data-testid="cta-education"
                  >
                    {EDUCATION_PLAN.cta}
                  </button>
                </div>
              </div>
            </article>
          </Reveal>
        </section>

        {educationOpen && (
          <EducationEnquiryModal
            onClose={() => setEducationOpen(false)}
          />
        )}

        {/* Account explanation */}
        <Reveal className="mb-16">
          <section
            className="neo-card bg-secondary/50 max-w-4xl mx-auto"
            aria-labelledby="account-structure-heading"
            data-testid="parent-note"
          >
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-xl bg-primary text-white border-2 border-foreground flex items-center justify-center shrink-0">
                <Shield size={22} weight="bold" />
              </div>

              <div>
                <h2
                  id="account-structure-heading"
                  className="font-heading font-black text-xl mb-2"
                >
                  How parent and student accounts work
                </h2>

                <p className="text-sm text-muted-foreground leading-relaxed">
                  A parent creates the main account and manages the
                  subscription. Each child receives a separate student
                  login for lessons, questions and quizzes. Early
                  Access includes the student progress dashboard.
                  Complete adds the more detailed parent readiness
                  dashboard, weak-area reporting and study controls —
                  and can cover two children on one subscription.
                </p>
              </div>
            </div>
          </section>
        </Reveal>

        {/* FAQ */}
        <section
          className="max-w-4xl mx-auto mb-16"
          aria-labelledby="pricing-faq-heading"
        >
          <Reveal className="text-center mb-9">
            <h2
              id="pricing-faq-heading"
              className="text-3xl font-heading font-black mb-3"
            >
              Pricing questions
            </h2>

            <p className="text-muted-foreground">
              Important information about free access, billing and
              plan features.
            </p>
          </Reveal>

          <div className="space-y-4">
            {[
              {
                question:
                  'Does the free sampler become a paid subscription?',
                answer:
                  'No. The free sampler does not require a payment card and does not automatically convert into a paid plan. When the seven days end, you can decide whether to subscribe.',
              },
              {
                question:
                  'Can I cancel a paid plan at any time?',
                answer:
                  'Yes. You can cancel a monthly or annual subscription and retain access until the end of the current paid billing period.',
              },
              {
                question:
                  'How does marking work?',
                answer:
                  'Each marked answer uses one allowance. The free sampler includes five marked answers, Essentials includes 50 each month, and Complete includes unlimited eligible marking subject to fair-use and platform terms.',
              },
              {
                question:
                  'Which plan includes the detailed parent dashboard?',
                answer:
                  'Complete includes the detailed parent GCSE-readiness dashboard, Foundation and Higher readiness indicators, weak-area reporting and study controls. Essentials includes the student progress dashboard.',
              },
              {
                question:
                  'How many pupil accounts can I add to a parent plan?',
                answer:
                  'Essentials covers one child. Complete can be switched to cover two children on the same subscription — choose the number of children on the Complete card before checkout.',
              },
              {
                question:
                  'Can I add another child later?',
                answer:
                  'Yes. Switch Complete to "2 children" at any time before checkout, or contact the team to change an existing subscription.',
              },
              {
                question:
                  'How does annual billing work?',
                answer:
                  'Annual plans are charged once per year. The page shows the total annual charge, its approximate monthly equivalent and the amount saved compared with paying monthly for 12 months.',
              },
              {
                question:
                  'Is the Education Provider plan free?',
                answer:
                  'Education Provider pricing is tailored to each school, trust or tutoring centre. The cost depends on learner numbers, required features and implementation support. Request a proposal for an accurate quotation.',
              },
            ].map((faq) => (
              <Reveal
                key={faq.question}
                delay={0.02}
              >
                <details className="neo-card group">
                  <summary className="cursor-pointer list-none flex items-center justify-between gap-4 font-heading font-black text-lg">
                    <span>{faq.question}</span>

                    <span
                      className="w-8 h-8 rounded-full border-2 border-foreground flex items-center justify-center text-xl leading-none group-open:rotate-45 transition-transform shrink-0"
                      aria-hidden="true"
                    >
                      +
                    </span>
                  </summary>

                  <p className="text-muted-foreground text-sm leading-relaxed mt-4 pr-10">
                    {faq.answer}
                  </p>
                </details>
              </Reveal>
            ))}
          </div>
        </section>

        {/* Final CTA */}
        <Reveal>
          <section className="neo-card relative overflow-hidden text-center max-w-4xl mx-auto p-8 md:p-11">
            <div
              className="absolute inset-x-0 top-0 h-2 bg-gradient-to-r from-indigo-500 via-accent to-emerald-500"
              aria-hidden="true"
            />

            <div className="w-14 h-14 rounded-2xl bg-primary text-white border-2 border-foreground flex items-center justify-center mx-auto mb-5 shadow-[3px_3px_0px_0px_rgb(var(--9ready-shadow))]">
              <Rocket size={28} weight="fill" />
            </div>

            <h2 className="text-3xl sm:text-4xl font-heading font-black mb-4">
              Start exploring Grade9Ready without paying
            </h2>

            <p className="text-muted-foreground max-w-2xl mx-auto mb-7 leading-relaxed">
              Use the seven-day sampler to explore selected lessons,
              Foundation Problem Mode, progress tracking and AI
              feedback before choosing a paid plan.
            </p>

            <div className="flex flex-col sm:flex-row justify-center gap-3">
              <Link
                to="/signup?role=parent"
                onClick={() =>
                  trackCta(
                    'start_free_sampler',
                    'pricing_final_cta',
                  )
                }
                className="neo-btn inline-flex items-center justify-center gap-2"
                data-testid="cta-bottom"
              >
                Create Parent Account
                <ArrowRight size={18} weight="bold" />
              </Link>

              <Link
                to="/free-lesson"
                onClick={() =>
                  trackCta(
                    'try_free_lesson',
                    'pricing_final_cta',
                  )
                }
                className="neo-btn-secondary inline-flex items-center justify-center gap-2"
              >
                Try a Free Lesson
                <Brain size={18} weight="bold" />
              </Link>
            </div>
          </section>
        </Reveal>
      </main>
    </div>
  );
}